// ShowEditItemModal.jsx
import React, { useState, useEffect, useRef } from "react";
import swal from "sweetalert";
import { toast } from "react-toastify";
import makeAnimated from "react-select/animated";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";

import { customStyles } from "../../../Bookclosure/Components/AddTaskModal";
import commoncss from "./../../css/common.module.css";
import buttoncss from "./../../css/button.module.css";
import libcss from "./../../css/library.module.css";
import Dropdown from "./../../elements/Dropdown";
import SuccessPopup from "../../elements/SuccessPopup";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/formulaPlus.svg";
import { ReactComponent as AddPlusIcon } from "../../assets/icons/grayPlus.svg";
import { ReactComponent as MinusIcon } from "../../assets/icons/MinusIcon.svg";
import { ReactComponent as MultiplyIcon } from "../../assets/icons/MultiplyIcon.svg";
import { ReactComponent as DivisionIcon } from "../../assets/icons/DivisionIcon.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/AddElement.svg";
import InputGroup from "./../../elements/InputGroup";
import CustomDropdownIndicator from "../../elements/CustomDropdownIndicator";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import adminPortalCss from "../../css/adminportal.module.css";
import sidePanelStyles from "../../css/cashFlowTable.module.css";

import { TbPlus, TbMinus, TbX, TbDivide } from "react-icons/tb";

const operatorOptions = [
  { value: "positive", label: "plus", symbol: "+", Icon: PlusIcon },
  { value: "negative", label: "minus", symbol: "−", Icon: MinusIcon },
  { value: "multiply", label: "multiply", symbol: "×", Icon: MultiplyIcon },
  { value: "divide", label: "divide", symbol: "÷", Icon: DivisionIcon },
];

const OPERATORS = ["positive", "negative", "multiply", "divide"];

const OperatorIcon = ({ type, size = 18, strokeWidth = 2 }) => {
  switch(type) {
    case "positive": return <TbPlus size={size} strokeWidth={strokeWidth} />;
    case "negative": return <TbMinus size={size} strokeWidth={strokeWidth} />;
    case "multiply": return <TbX size={size} strokeWidth={strokeWidth} />;
    case "divide": return <TbDivide size={size} strokeWidth={strokeWidth} />;
    default: return <TbPlus size={size} strokeWidth={strokeWidth} />;
  }
};

const OperatorBtn = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
      {open && (
        <div 
          className={sidePanelStyles.operatorPopup} 
          style={{ 
            position: "absolute", 
            top: "50%", 
            left: "50%", 
            transform: "translate(-50%, -50%)", 
            zIndex: 99
          }}
        >
          {OPERATORS.map((op) => (
            <div
              key={op}
              className={`${sidePanelStyles.operatorItem} ${value === op ? sidePanelStyles.activeOperator : ""}`}
              onClick={() => { 
                onChange(op); 
                setOpen(false); 
              }}
              style={{ padding: "4px" }}
            >
              <OperatorIcon type={op} size={18} />
            </div>
          ))}
        </div>
      )}
      <div
        className={sidePanelStyles.addBtn}
        onClick={() => setOpen((o) => !o)}
        title="Change operator"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          width: window.location.pathname.includes("/library/") ? "32px" : undefined,
          height: window.location.pathname.includes("/library/") ? "32px" : undefined,
        }}
      >
        <OperatorIcon type={value} size={15} />
      </div>
    </div>
  );
};

const Checkbox = ({ label, checked, onChange }) => {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", userSelect: "none" }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange && onChange(e.target.checked)}
        style={{ accentColor: "#3AB67D", margin: 0, width: "16px", height: "16px" }}
      />
      <span style={{ fontSize: "14px", color: "#33584E", fontWeight: "500" }}>{label}</span>
    </label>
  );
};

const ShowEditItemModal = ({ groupingData, item, level, context = {}, onSave, onClose }) => {
  // keep original UI/state names
  const [selectedItem, setSelectedItem] = useState(item?.lineType ?? "line item");
  const [itemName, setItemName] = useState(item?.name ?? "");
  const [lineItemNature, setLineItemNature] = useState(item?.nature ?? "credit");

  const [isBold, setIsBold] = useState(item?.isBold || false);
  const [isTotal, setIsTotal] = useState(item?.formulaType === "total");
  const [isSubtotal, setIsSubtotal] = useState(item?.formulaType === "subtotal");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleTotalToggle = (val) => {
    setIsTotal(val);
    if (val) {
      setIsBold(true);
      setIsSubtotal(false);
    }
  };

  const handleSubtotalToggle = (val) => {
    setIsSubtotal(val);
    if (val) {
      setIsBold(true);
      setIsTotal(false);
    }
  };

  // each formula element stores { element: id, value: optionObject, operator }
  const [formulaItems, setFormulaItems] = useState([]);
  const [pendingOperator, setPendingOperator] = useState("positive");

  // 🔸 LEVEL TO MATCH = item.level (fallback to level prop)
  const levelToMatch = item?.level ?? level;

  // 🔹 restrict the search ROOT to the branch we are editing (that particular object)
  const getRootItemsForFormula = () => {
    if (!Array.isArray(groupingData)) return [];

    // Level 1 → whole top-level list
    if (levelToMatch === 1) return groupingData;

    // Level 2 → subgroups of the selected level-1 node
    if (levelToMatch === 2) {
      const l1id = context?.l1id;
      if (!l1id) return groupingData;
      const l1 = groupingData.find((g) => g._id === l1id);
      return Array.isArray(l1?.subgroup) ? l1.subgroup : [];
    }

    // Level 3 → subgroups of the selected level-2 node inside the correct level-1
    if (levelToMatch === 3) {
      const l1id = context?.l1id;
      const l2id = context?.l2id;
      if (!l1id || !l2id) return groupingData;

      const l1 = groupingData.find((g) => g._id === l1id);
      const l2 = Array.isArray(l1?.subgroup)
        ? l1.subgroup.find((sg) => sg._id === l2id)
        : null;

      return Array.isArray(l2?.subgroup) ? l2.subgroup : [];
    }

    // fallback
    return groupingData;
  };

  // Build selectable items for formula builder (mirrors AddItemModal.extractItems)
  // 🔧 CHANGED: added rootCategory param and pass it into traverse()
  // BEFORE: had category filtering that killed level-3 items
// const extractItems = (items = [], lvlToMatch, categoryToMatch, rootCategory = null) => {
const extractItems = (items = [], lvlToMatch) => {
  const result = [];
  const seen = new Set();

  const traverse = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      // ✅ only same level, and only line items (skip formulas like "Total")
      if (node.level === lvlToMatch) {
        const key = `${node.level}||${node.name}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({ label: node.name, value: node.name });
        }
      }

      if (Array.isArray(node.subgroup) && node.subgroup.length) {
        traverse(node.subgroup);
      }
    }
  };

  traverse(items);
  return result;
};

  const guessCategoryForFormula = () => {
    if (!groupingData) return null;
    if (context?.category) return context.category;
    const l1id = context?.l1id ?? null;
    if (l1id) {
      const l1 = groupingData.find((g) => g._id === l1id);
      if (l1 && l1.category) return l1.category;
    }
    if (item?.category) return item.category;
    return null;
  };

const formulaItemList = extractItems(
  getRootItemsForFormula(),
  levelToMatch
);


  // Helper: get canonical option object for a name (or for an incoming option-like object)
  const canonicalOptionFor = (incoming) => {
    if (!incoming && incoming !== "") return null;
    // if incoming is an object with label/value, try to match by value or label
    if (typeof incoming === "object") {
      const name = incoming.value ?? incoming.label ?? null;
      if (name == null) return incoming;
      const found = formulaItemList.find(
        (o) => o.value === name || o.label === name
      );
      return found ?? null;
    }
    // primitive: string -> match
    if (typeof incoming === "string") {
      const found = formulaItemList.find(
        (o) => o.value === incoming || o.label === incoming
      );
      return found ?? null;
    }
    return null;
  };

  // initialize formulaItems from item.formula (if present)
  useEffect(() => {
    // populate basic fields
    setSelectedItem(item?.lineType ?? "line item");
    setItemName(item?.name ?? "");
    setLineItemNature(item?.nature ?? "credit");
    setIsBold(item?.isBold || false);
    setIsTotal(item?.formulaType === "total");
    setIsSubtotal(item?.formulaType === "subtotal");

    if (
      (item?.formula && Array.isArray(item.formula) && item.formula.length > 0) ||
      item?.lineType === "formula"
    ) {
      // map saved formula names into option objects (from formulaItemList), filter if missing
      const mappedRaw = Array.isArray(item?.formula) && item.formula.length > 0
        ? item.formula.map((f, idx) => ({
            element: Date.now() + idx,
            value: canonicalOptionFor(f.name ?? ""),
            operator:
              f.sign === "-" || f.sign === "−" || f.sign === "negative"
                ? "negative"
                : f.sign === "*" || f.sign === "×" || f.sign === "multiply"
                ? "multiply"
                : f.sign === "/" || f.sign === "÷" || f.sign === "divide"
                ? "divide"
                : "positive",
          }))
        : [];
      
      const mapped = mappedRaw.filter((x) => x.value !== null);
      if (mapped.length === 0) {
        mapped.push({ element: Date.now(), value: null, operator: "positive" });
      }

      setFormulaItems(mapped);
    } else {
      setFormulaItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, groupingData]);

  const addFormulaItem = () => {
    setFormulaItems((prev) => [
      ...prev,
      {
        element: Date.now() + Math.floor(Math.random() * 1000),
        value: null,
        operator: pendingOperator,
      },
    ]);
  };

  // Accept option object OR primitive; normalize into option object for state
  const updateFormulaItem = (index, incoming) => {
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], value: canonicalOptionFor(incoming) };
      return copy;
    });
  };

  const updateFormulaOperator = (index, operator) => {
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], operator };
      return copy;
    });
  };

  const removeFormulaItem = (index) => {
    if (!window.confirm("Are you sure you want to delete this element?")) {
      return;
    }
    setFormulaItems((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      if (copy.length === 0) {
        copy.push({ element: Date.now() + Math.floor(Math.random() * 1000), value: "", operator: "positive" });
      }
      return copy;
    });
    setShowSuccessPopup(true);
  };

  const formulaDisplayNode = (() => {
    if (!formulaItems || formulaItems.length === 0) return "";
    const pieces = formulaItems.map((it) => {
      if (!it || !it.value) return "(?)";
      if (typeof it.value === "string") return it.value;
      if (it.value.label) return it.value.label;
      return it.value.value ?? "(?)";
    });
    
    const elements = [];
    elements.push(<React.Fragment key="first">{pieces[0] || ""}</React.Fragment>);
    
    for (let i = 1; i < pieces.length; i++) {
      elements.push(
        <React.Fragment key={`op-${i}`}>
          {" "}
          <span style={{ display: "inline-flex", alignItems: "center", gap: "2px", verticalAlign: "middle" }}>
            <span style={{ color: "#99ABA7" }}>(</span>
            <span style={{ display: "flex", alignItems: "center", transform: "translateY(-1.5px)"}}><OperatorIcon type={formulaItems[i].operator} strokeWidth={3}/></span>
            <span style={{ color: "#99ABA7" }}>)</span>
          </span>{" "}
          {pieces[i]}
        </React.Fragment>
      );
    }
    return elements;
  })();

  const handleSave = () => {
    if (!itemName || itemName.trim() === "") {
      toast.error("Please enter a name");
      return;
    }

    const updated = {};
    if (itemName !== (item?.name ?? "")) updated.name = itemName;
    if (selectedItem !== (item?.lineType ?? "line item"))
      updated.lineType = selectedItem;
    if (
      selectedItem === "line item" &&
      lineItemNature !== (item?.nature ?? "credit")
    ) {
      updated.nature = lineItemNature;
    }

    if (selectedItem === "formula") {
      const returnFormulaItems = formulaItems
        .filter(
          (x) =>
            x.value &&
            (x.value.value || x.value.label || typeof x.value === "string")
        )
        .map((x) => ({
          name:
            typeof x.value === "string"
              ? x.value
              : x.value.value ?? x.value.label ?? "",
          sign: x.operator ?? "positive",
        }));
      updated.formula = returnFormulaItems;
    }
    updated.isBold = isBold || isTotal || isSubtotal;
    updated.formulaType = isTotal ? "total" : isSubtotal ? "subtotal" : null;

    onSave && onSave(updated);
  };

  return (
    <div
      className={sidePanelStyles.sidePanel}
      style={{
        width: selectedItem === "formula" && formulaItems.length > 1 ? "840px" : "650px",
        minWidth: selectedItem === "formula" && formulaItems.length > 1 ? "840px" : "650px",
        maxWidth: selectedItem === "formula" ? "95vw" : "650px",
      }}
    >
      <div className={sidePanelStyles.panelHeader}>
        <span style={{ fontSize: "18px" }}>Edit {selectedItem === "formula" ? "Formula" : "Line Item"}</span>
        <span 
          className={sidePanelStyles.close} 
          onClick={() => onClose && onClose()}
        >
          ✕
        </span>
      </div>

      <div className={sidePanelStyles.panelBody} style={{ padding: "20px" }}>
        <div className={adminPortalCss.radioButtonSection} style={{ marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap", gap: "24px" }} >
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="responsibilities"
            value={selectedItem}
            onChange={(e) => setSelectedItem(e.target.value)}
            className={adminPortalCss.radioSection}
            style={{ display: "flex", flexDirection: "row", gap: "16px", margin: 0 }}
          >
            <FormControlLabel
              value="line item"
              className={adminPortalCss.radioLabel}
              control={
                <Radio
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "#D6D6D6",
                    "&.Mui-checked": { color: "#3AB67D" },
                  }}
                />
              }
              label={<span style={{ fontSize: "15px" }}>Line Item</span>}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight: 500,
                  textTransform: "none",
                  color: "#111111",
                  fontSize: "14px",
                },
              }}
            />
            <FormControlLabel
              className={adminPortalCss.radioLabel}
              value="formula"
              control={
                <Radio
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: 18 },
                    color: "#D6D6D6",
                    "&.Mui-checked": { color: "#3AB67D" },
                  }}
                />
              }
              label={<span style={{ fontSize: "15px" }}>Formula</span>}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight: 500,
                  textTransform: "none",
                  color: "#111111",
                  fontSize: "14px",
                },
              }}
            />
          </RadioGroup>

          {(selectedItem === "formula" || selectedItem === "line item") && (
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0, marginLeft: "auto" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: isTotal || isSubtotal ? "not-allowed" : "pointer", fontSize: "14px", fontWeight: "500", color: "#33584E" }}>
                <input
                  type="checkbox"
                  checked={isBold || isTotal || isSubtotal}
                  onChange={(e) => {
                    if (isTotal || isSubtotal) return;
                    setIsBold(e.target.checked);
                  }}
                  onClick={(e) => {
                    if (isTotal || isSubtotal) {
                      e.preventDefault();
                    }
                  }}
                  style={{ accentColor: "#3AB67D", margin: 0, width: "16px", height: "16px", cursor: isTotal || isSubtotal ? "not-allowed" : "pointer" }}
                />
                Bold
              </label>
              <Checkbox
                label="Total"
                checked={isTotal}
                onChange={handleTotalToggle}
              />
              <Checkbox
                label="Subtotal"
                checked={isSubtotal}
                onChange={handleSubtotalToggle}
              />
            </div>
          )}
        </div>

        {selectedItem === "line item" && (
          <>
            <InputGroup
              class_="mb-2"
              label="Line Item Name"
              labelStyle={{ fontSize: "15px" }}
              inputs={[
                <>
                  <input
                    name="line item name"
                    value={itemName ?? ""}
                    onChange={(e) => setItemName(e.target.value)}
                    placeholder="Enter line item name"
                  />
                </>,
              ]}
            />
            {levelToMatch === 1 && (
              <div className={commoncss.dFlexJustifysb} style={{ marginTop: "20px" }}>
                <span className={`${commoncss.label2} ${commoncss.fs15}`} style={{ marginLeft: "5px" }}>
                  Nature of Line Item
                </span>
                <div className={adminPortalCss.radioButtonSection} style={{ marginLeft: "5px" }}>
                  <RadioGroup
                    row
                    name="lineItemNature"
                    value={lineItemNature}
                    onChange={(e) => setLineItemNature(e.target.value)}
                    className={adminPortalCss.radioSection}
                  >
                    <FormControlLabel
                      value="credit"
                      className={adminPortalCss.radioLabel}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: "#D6D6D6",
                            "&.Mui-checked": { color: "#3AB67D" },
                          }}
                        />
                      }
                      label={<span style={{ fontSize: "15px" }}>Credit</span>}
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 500,
                          textTransform: "none",
                          color: "#111111",
                          fontSize: "15px",
                        },
                      }}
                    />
                    <FormControlLabel
                      value="debit"
                      className={adminPortalCss.radioLabel}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 18 },
                            color: "#D6D6D6",
                            "&.Mui-checked": { color: "#3AB67D" },
                          }}
                        />
                      }
                      label={<span style={{ fontSize: "15px" }}>Debit</span>}
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 500,
                          textTransform: "none",
                          color: "#111111",
                          fontSize: "13px",
                        },
                      }}
                    />
                  </RadioGroup>
                </div>
              </div>
            )}
          </>
        )}

        {selectedItem === "formula" && (
          <div >
            <div style={{ width: "100%" }}>
              <InputGroup
                class_="mb-2"
                label="Formula Name"
                labelStyle={{ fontSize: "15px" }}
                inputs={[
                  <>
                    <input
                      name="Formula name"
                      value={itemName ?? ""}
                      onChange={(e) => setItemName(e.target.value)}
                      placeholder="Enter formula name"
                    />
                  </>,
                ]}
              />
             
              <div className={`${libcss.displayFormula} mt-4`} style={{ whiteSpace: "normal", wordBreak: "break-word" , margin: "30px 0"}}>
                <span className={`${commoncss.label5} ${commoncss.fw500}`} style={{ display: "block" }}>
                  Formula ={" "}
                  {formulaDisplayNode || "(no formula selected)"}
                </span>
              </div>
            </div>
            {/* <span className={`input-label ml-1`}>Build Formula</span> */}

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "20px 12px",
                flexWrap: "wrap",
                marginTop: "16px"
              }}
            >
              {formulaItems.map((fitem, index) => (
                <React.Fragment key={fitem.element ?? index}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px"
                    }}
                  >
                    <div className={libcss.formulaCard} style={{ margin: 0, width: "210px", height: "105px", padding: "10px", position: "relative" }}>
                      <CloseIcon
                        className={libcss.formulaCardCloseIcon}
                        style={{ position: "absolute", top: "8px", right: "8px", cursor: "pointer", width: "12px", height: "12px" }}
                        onClick={() => removeFormulaItem(index)}
                      />
                      <span
                        className={`${commoncss.label3} ${commoncss.fw600}`}
                      >
                        LEVEL {levelToMatch}
                      </span>

                      <div style={{ width: "100%" }}>
                        <Dropdown
                          class2_={commoncss.bgWhite}
                          options={formulaItemList}
                          name="formula"
                          selected={fitem.value}
                          onSelect={(value) => updateFormulaItem(index, value)}
                          placeHolder="Select item"
                          searchable={true}
                          wrapText={true}
                        />
                      </div>
                    </div>

                    {index < formulaItems.length - 1 ? (
                      <div style={{ marginTop: "10px" }}>
                        <OperatorBtn
                          value={formulaItems[index + 1].operator}
                          onChange={(v) => updateFormulaOperator(index + 1, v)}
                        />
                      </div>
                    ) : (
                      <div style={{ marginTop: "10px" }}>
                        <OperatorBtn
                          value={pendingOperator}
                          onChange={(v) => setPendingOperator(v)}
                        />
                      </div>
                    )}
                  </div>
                </React.Fragment>
              ))}

              <div
                className={sidePanelStyles.addElementBox1}
                onClick={addFormulaItem}
                style={{ width: "210px", minWidth: "210px", height: "105px", marginTop: "0px", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <p style={{ margin: 0, fontSize: "15px", fontWeight: 500, color: "#B3C0BD" }}><AddIcon width={16} height={16} style={{ marginTop: "-2px" }}/> Add Element</p>
              </div>
            </div>

            {/* <div className={`${libcss.displayFormula} mt-3`} style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
              <span className={`${commoncss.greyLabel} ${commoncss.fw500}`}>
                Formula
              </span>
              <span className={`${commoncss.label5} ${commoncss.fw500}`} style={{ display: "block", marginTop: "4px" }}>
                Formula Name ={" "}
                {formulaDisplayNode || "(no formula selected)"}
              </span>
            </div> */}
          </div>
        )}
      </div>

      <div className={sidePanelStyles.footer}>
        <button 
          className={sidePanelStyles.cancelBtn} 
          onClick={() => onClose && onClose()}
        >
          Cancel
        </button>
        <button 
          className={sidePanelStyles.applyBtn} 
          onClick={handleSave}
        >
          Save
        </button>
      </div>
      <SuccessPopup
        isOpen={showSuccessPopup}
        message="Item deleted successfully."
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
  );
};

export default ShowEditItemModal;